import { useMemo, useRef, useState, useEffect } from "react";
import { useFilterContext } from "../../ContextManager";
import { deleteCustomAirport } from "../../../utils/customAirportFunctions";
import { delay } from "../../../utils/utilityFunctions";
import { countriesListObj } from "../../../utils/variables";
import { fetchServerData } from "../../../utils/fetchServerData";
import { getClosestCountryIso } from "../../../utils/countryFromCoordinates";

// MUI components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Icons
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";

function getAirportFilterIso(airport) {
  return airport?.region?.iso || airport?.country?.iso || "";
}

function getAirportLoadedState(airport, filters) {
  if (!filters?.country || filters.country.length === 0) return true;

  return (
    filters.country.includes(airport?.country?.iso ?? "") ||
    filters.country.includes(airport?.region?.iso ?? "")
  );
}

function getCountryLabelFromIso(iso) {
  if (!iso) return "Unknown";
  const lookupKey = iso.replace(/-/g, "_");
  return countriesListObj[lookupKey] || iso;
}

function isLegacyCustomAirport(airport) {
  const countryIso = airport?.country?.iso;
  const regionIso = airport?.region?.iso;

  const normalizedCountryIso = countryIso?.replace(/-/g, "_");
  const normalizedRegionIso = regionIso?.replace(/-/g, "_");

  const countryIsoExistsInList = normalizedCountryIso
    ? Object.prototype.hasOwnProperty.call(
        countriesListObj,
        normalizedCountryIso,
      )
    : false;

  const regionIsoExistsInList = normalizedRegionIso
    ? Object.prototype.hasOwnProperty.call(
        countriesListObj,
        normalizedRegionIso,
      )
    : false;

  return (
    !airport?.country ||
    !countryIso ||
    countryIso === "XX" ||
    airport.country.name === "Custom Airport" ||
    countryIso.includes("-") ||
    countryIso.includes("_") ||
    !countryIsoExistsInList ||
    (regionIso != null && !regionIsoExistsInList)
  );
}

function buildCountryRegionFromIso(iso) {
  const lookupKey = iso.replace(/-/g, "_");
  const selectedLabel = countriesListObj[lookupKey] || "";
  const baseIso = iso.split("-")[0];

  const stripRegionSuffix = (label, base) => {
    if (!label) return "";
    if (base === "US") return label.replace(/,\s*USA\s*$/i, "").trim();
    if (base === "CA") return label.replace(/,\s*Canada\s*$/i, "").trim();
    return label.trim();
  };

  const countryName =
    baseIso === "US"
      ? "United States"
      : baseIso === "CA"
        ? "Canada"
        : selectedLabel;

  return {
    country: {
      iso: baseIso,
      name: countryName,
    },
    region: iso.includes("-")
      ? {
          iso,
          name: stripRegionSuffix(selectedLabel, baseIso),
        }
      : null,
  };
}

export function CustomAirportManagerModal({ open, onClose }) {
  const {
    filters,
    customAirports,
    updateCustomAirports,
    updateCustomAirportForm,
    updateEditingAirport,
    updateMapFocusRequest,
    geoJsonData,
  } = useFilterContext();

  const [searchText, setSearchText] = useState("");
  const [countryFilter, setCountryFilter] = useState(null);
  const [loadedFilter, setLoadedFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const processedLegacyIdsRef = useRef(new Set());
  const hasRunLegacyRepairThisOpenRef = useRef(false);

  const [isUpdatingLegacy, setIsUpdatingLegacy] = useState(false);
  const [legacyUpdateSummary, setLegacyUpdateSummary] = useState({
    successCount: 0,
    failureCount: 0,
    skippedCount: 0,
    totalCount: 0,
  });
  const [showLegacyUpdateResult, setShowLegacyUpdateResult] = useState(false);

  const allCustomAirports = useMemo(() => {
    if (!customAirports?.airports?.length) return [];
    return [...customAirports.airports];
  }, [customAirports]);

  const countryOptions = useMemo(() => {
    const uniqueIsos = new Set();

    allCustomAirports.forEach((airport) => {
      const iso = getAirportFilterIso(airport);
      if (iso) uniqueIsos.add(iso);
    });

    const options = [...uniqueIsos].map((iso) => ({
      code: iso,
      label: getCountryLabelFromIso(iso),
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [allCustomAirports]);

  const filteredAirports = useMemo(() => {
    let list = [...allCustomAirports];

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      list = list.filter((airport) => {
        const name = airport.name?.toLowerCase() || "";
        const description = airport.description?.toLowerCase() || "";
        const countryLabel = getCountryLabelFromIso(
          getAirportFilterIso(airport),
        ).toLowerCase();

        return (
          name.includes(query) ||
          description.includes(query) ||
          countryLabel.includes(query)
        );
      });
    }

    if (countryFilter?.code) {
      list = list.filter(
        (airport) => getAirportFilterIso(airport) === countryFilter.code,
      );
    }

    if (loadedFilter !== "all") {
      list = list.filter((airport) => {
        const isLoaded = getAirportLoadedState(airport, filters);
        return loadedFilter === "loaded" ? isLoaded : !isLoaded;
      });
    }

    list.sort((a, b) => {
      if (sortBy === "name-asc") {
        return (a.name || "").localeCompare(b.name || "");
      }

      if (sortBy === "name-desc") {
        return (b.name || "").localeCompare(a.name || "");
      }

      if (sortBy === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      }

      if (sortBy === "country") {
        return getCountryLabelFromIso(getAirportFilterIso(a)).localeCompare(
          getCountryLabelFromIso(getAirportFilterIso(b)),
        );
      }

      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

    return list;
  }, [
    allCustomAirports,
    searchText,
    countryFilter,
    loadedFilter,
    sortBy,
    filters,
  ]);

  const visibleSelectedCount = useMemo(() => {
    const visibleIds = new Set(filteredAirports.map((airport) => airport._id));
    return selectedIds.filter((id) => visibleIds.has(id)).length;
  }, [filteredAirports, selectedIds]);

  const loadedCount = useMemo(() => {
    return allCustomAirports.filter((airport) =>
      getAirportLoadedState(airport, filters),
    ).length;
  }, [allCustomAirports, filters]);

  const allVisibleSelected =
    filteredAirports.length > 0 &&
    filteredAirports.every((airport) => selectedIds.includes(airport._id));

  useEffect(() => {
    if (!open) return;
    if (hasRunLegacyRepairThisOpenRef.current) return;
    if (!geoJsonData?.features?.length) return;
    if (!customAirports?.airports?.length) return;

    hasRunLegacyRepairThisOpenRef.current = true;

    const repairLegacyAirports = async () => {
      const legacyAirports = customAirports.airports.filter(
        (airport) =>
          isLegacyCustomAirport(airport) &&
          !processedLegacyIdsRef.current.has(airport._id),
      );

      if (legacyAirports.length === 0) return;

      setIsUpdatingLegacy(true);

      let successCount = 0;
      let failureCount = 0;
      let skippedCount = 0;

      try {
        for (const airport of legacyAirports) {
          processedLegacyIdsRef.current.add(airport._id);

          const [lon, lat] = airport?.location?.coordinates || [];
          if (lon == null || lat == null) {
            skippedCount += 1;
            continue;
          }

          const matchedIso = getClosestCountryIso(lat, lon, geoJsonData);

          if (!matchedIso) {
            skippedCount += 1;
            continue;
          }

          const normalized = buildCountryRegionFromIso(matchedIso);

          try {
            await fetchServerData("/updatecustomlocation", {
              method: "PUT",
              body: JSON.stringify({
                _id: airport._id,
                country: normalized.country,
                region: normalized.region,
              }),
            });

            successCount += 1;

            updateCustomAirports((prev) => ({
              airports: prev.airports.map((a) =>
                a._id === airport._id
                  ? {
                      ...a,
                      country: normalized.country,
                      region: normalized.region,
                    }
                  : a,
              ),
              routes: prev.routes,
            }));
          } catch (error) {
            failureCount += 1;
            console.error(
              "[LegacyRepair] update failed for airport",
              airport._id,
              error,
            );
          }
        }

        setLegacyUpdateSummary({
          successCount,
          failureCount,
          skippedCount,
          totalCount: legacyAirports.length,
        });

        if (legacyAirports.length > 0) {
          setShowLegacyUpdateResult(true);
        }
      } finally {
        setIsUpdatingLegacy(false);
      }
    };

    repairLegacyAirports();
  }, [
    open,
    geoJsonData,
    customAirports?.airports?.length,
    updateCustomAirports,
  ]);

  const handleToggleAirport = (airportId) => {
    setSelectedIds((prev) =>
      prev.includes(airportId)
        ? prev.filter((id) => id !== airportId)
        : [...prev, airportId],
    );
  };

  const handleSelectAllVisible = () => {
    const visibleIds = filteredAirports.map((airport) => airport._id);

    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleEdit = async (airport) => {
    onClose();
    updateCustomAirportForm(false);
    updateEditingAirport(airport);
    await delay(100);
    updateCustomAirportForm(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;

    setIsDeleting(true);

    try {
      const idsToDelete = [...selectedIds];

      for (const airportId of idsToDelete) {
        await deleteCustomAirport(airportId);
      }

      updateCustomAirports((prev) => ({
        airports: prev.airports.filter(
          (airport) => !idsToDelete.includes(airport._id),
        ),
        routes: prev.routes.filter(
          (route) =>
            !idsToDelete.includes(route.airport_1) &&
            !idsToDelete.includes(route.airport_2),
        ),
      }));

      setSelectedIds([]);
      setConfirmDeleteOpen(false);
    } catch (error) {
      console.error("Error deleting custom airports:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSingleDelete = (airportId) => {
    setSelectedIds([airportId]);
    setConfirmDeleteOpen(true);
  };

  const handleCloseModal = () => {
    if (isDeleting || isUpdatingLegacy) return;
    setSelectedIds([]);
    setSearchText("");
    setCountryFilter(null);
    setLoadedFilter("all");
    setSortBy("newest");
    setLegacyUpdateSummary({
      successCount: 0,
      failureCount: 0,
      skippedCount: 0,
      totalCount: 0,
    });
    setShowLegacyUpdateResult(false);
    processedLegacyIdsRef.current.clear();
    hasRunLegacyRepairThisOpenRef.current = false;
    onClose();
  };

  const handleShowOnMap = (airport) => {
    const coordinates = airport?.location?.coordinates;

    if (!coordinates || coordinates.length < 2) return;

    const [lon, lat] = coordinates;

    updateMapFocusRequest({
      coordinates: { lat, lng: lon },
      zoomLevel: 10,
      requestId: Date.now(),
      airportId: airport._id,
    });

    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
          }}
        >
          <span>Manage Custom Airports</span>
          <IconButton
            onClick={handleCloseModal}
            disabled={isDeleting || isUpdatingLegacy}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            overflow: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            <Chip
              label={`Total: ${allCustomAirports.length}`}
              size="small"
              color="primary"
            />
            <Chip
              label={`Loaded on map: ${loadedCount}`}
              size="small"
              color="success"
            />
            {selectedIds.length > 0 && (
              <Chip
                label={`Selected: ${selectedIds.length}`}
                size="small"
                color="primary"
              />
            )}
            {isUpdatingLegacy && (
              <Chip
                label="Updating legacy airports..."
                size="small"
                color="warning"
              />
            )}
          </Box>

          {showLegacyUpdateResult && legacyUpdateSummary.totalCount > 0 && (
            <Alert
              severity={
                legacyUpdateSummary.failureCount > 0
                  ? "warning"
                  : legacyUpdateSummary.successCount > 0
                    ? "success"
                    : "info"
              }
              onClose={() => setShowLegacyUpdateResult(false)}
              sx={{ mb: 2 }}
            >
              {legacyUpdateSummary.successCount > 0 &&
              legacyUpdateSummary.failureCount === 0 &&
              legacyUpdateSummary.skippedCount === 0
                ? legacyUpdateSummary.successCount === 1
                  ? "Updated 1 legacy custom airport automatically."
                  : `Updated ${legacyUpdateSummary.successCount} legacy custom airports automatically.`
                : `Legacy airport update finished. Updated: ${legacyUpdateSummary.successCount}, Failed: ${legacyUpdateSummary.failureCount}, Skipped: ${legacyUpdateSummary.skippedCount}.`}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr 0.8fr 0.8fr" },
              gap: 1,
              mb: 2,
            }}
          >
            <TextField
              label="Search airports"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
            />

            <Autocomplete
              options={countryOptions}
              value={countryFilter}
              onChange={(event, newValue) => setCountryFilter(newValue)}
              getOptionLabel={(option) => option.label}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Filter by country" />
              )}
            />

            <TextField
              select
              label="Loaded status"
              size="small"
              value={loadedFilter}
              onChange={(e) => setLoadedFilter(e.target.value)}
              fullWidth
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="loaded">Loaded only</MenuItem>
              <MenuItem value="not-loaded">Not loaded only</MenuItem>
            </TextField>

            <TextField
              select
              label="Sort by"
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              fullWidth
            >
              <MenuItem value="newest">Newest first</MenuItem>
              <MenuItem value="oldest">Oldest first</MenuItem>
              <MenuItem value="name-asc">Name A-Z</MenuItem>
              <MenuItem value="name-desc">Name Z-A</MenuItem>
              <MenuItem value="country">Country A-Z</MenuItem>
            </TextField>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAllVisible}
              sx={{
                minWidth: 130,
                justifyContent: "center",
              }}
            >
              {allVisibleSelected ? "Deselect All" : "Select All"}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleClearSelection}
              disabled={selectedIds.length === 0}
              sx={{
                minWidth: 130,
                justifyContent: "center",
              }}
            >
              Clear Selection
            </Button>

            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              disabled={selectedIds.length === 0}
              onClick={() => setConfirmDeleteOpen(true)}
              sx={{
                minWidth: 145,
                justifyContent: "center",
              }}
            >
              Delete Selected
            </Button>

            <Box sx={{ ml: "auto", fontSize: 13, opacity: 0.7 }}>
              Showing {filteredAirports.length} airport
              {filteredAirports.length === 1 ? "" : "s"}
              {visibleSelectedCount > 0
                ? ` • ${visibleSelectedCount} selected in view`
                : ""}
            </Box>
          </Box>

          <Divider sx={{ mb: 1 }} />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: 1,
            }}
          >
            {filteredAirports.length === 0 ? (
              <Box sx={{ py: 3, textAlign: "center", opacity: 0.7 }}>
                No custom airports match the current filters.
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {filteredAirports.map((airport) => {
                  const airportIso = getAirportFilterIso(airport);
                  const countryLabel = getCountryLabelFromIso(airportIso);
                  const isLoaded = getAirportLoadedState(airport, filters);
                  const isSelected = selectedIds.includes(airport._id);

                  return (
                    <Box
                      key={airport._id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        gap: 1,
                        alignItems: "center",
                        p: 1.25,
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 1,
                        opacity: isLoaded ? 1 : 0.45,
                        backgroundColor: isSelected
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleAirport(airport._id)}
                      />

                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            flexWrap: "wrap",
                            mb: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {airport.name}
                          </Box>

                          <Chip
                            label={isLoaded ? "Loaded" : "Not Loaded"}
                            size="small"
                            color={isLoaded ? "success" : "default"}
                            variant="outlined"
                          />

                          {airportIso && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    pr: "2px",
                                  }}
                                >
                                  <span>{countryLabel}</span>
                                  <img
                                    loading="lazy"
                                    height="12"
                                    src={`https://flagcdn.com/h20/${airportIso
                                      .split(/[-_]/)[0]
                                      .toLowerCase()}.webp`}
                                    alt={`Flag of ${airportIso.split(/[-_]/)[0]}`}
                                  />
                                </Box>
                              }
                            />
                          )}

                          {airport.group && (
                            <Chip
                              label={`Group: ${airport.group}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        {airport.description && (
                          <Box
                            sx={{
                              fontSize: 13,
                              opacity: 0.75,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {airport.description}
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Show on map">
                          <IconButton onClick={() => handleShowOnMap(airport)}>
                            <MyLocationIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit airport">
                          <IconButton onClick={() => handleEdit(airport)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete airport">
                          <IconButton
                            onClick={() => handleSingleDelete(airport._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseModal}
            disabled={isDeleting || isUpdatingLegacy}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => (isDeleting ? null : setConfirmDeleteOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete custom airports?</DialogTitle>
        <DialogContent dividers>
          {selectedIds.length === 1 ? (
            <Box>
              This will delete 1 custom airport and remove any connected custom
              routes.
            </Box>
          ) : (
            <Box>
              This will delete {selectedIds.length} custom airports and remove
              any connected custom routes.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteSelected}
            disabled={isDeleting || selectedIds.length === 0}
            startIcon={
              isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />
            }
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
